import ClearAllIcon from '@mui/icons-material/ClearAll';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DeleteIcon from '@mui/icons-material/Delete';
import PreviewIcon from '@mui/icons-material/Preview';
import PublicIcon from '@mui/icons-material/Public';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import {
  Divider,
  Menu,
  MenuItem,
  MenuProps,
  useTheme,
  Box,
  CircularProgress,
} from '@mui/material';
import { SimpleConfirmDeleteModal } from 'components/Modals/SimpleConfirmDeleteModal';
import { getRecordRealId } from 'components/Search/components/SearchItemsProvider';
import { OpenInNewButton } from 'components/generic/OpenInNewButton';
import { getMiniMenuIconSx, miniMenuSx } from 'components/Minimenus/miniMenuStyles';
import { useCosmos } from 'providers/contexts/CosmosContext';
import React, {
  forwardRef,
  Fragment,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { useMutation, InfiniteData } from '@tanstack/react-query';
import { isAssetTypeEditorEnabled } from 'utility/functions';
import {
  ApiErrorResponse,
  BloxCustomDataProviderType,
  EditorialAssetSearchResultItem,
  FullEditorialAsset,
  PostEditorialAssetDeleteParams,
  PostEditorialAssetDeleteResult,
  PurgeEditorialAssetByIdParams,
  PurgeEditorialAssetByIdResult,
  RestoreEditorialAssetByIdParams,
  RestoreEditorialAssetByIdResult,
} from 'utility/types/dataProvider';
import { TGetListResult } from 'providers/contexts/extensions/functions/RequestBuilders';
import { BaseMiniMenuProps } from './MiniMenuSelector';
import {
  checkCreateAssetsPermission,
  checkDeleteAssetsPermission,
  checkSiteAdminPermission,
  PermissionInfo,
} from 'utility/permissions';
import { useTranslation } from 'react-i18next';
import { useDuplicateAsset } from 'components/Minimenus/hooks/useDuplicateAsset';
import { useAssetQuickLaunch } from 'components/editorial/AssetQuickLaunch/useAssetQuickLaunch';
import type { AssetQuickLaunchPrefill } from 'components/editorial/AssetQuickLaunch/types';
import { useExportImport } from 'components/ExportImport/ExportImportContext';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import { formatAssetString, truncateString } from 'utility/functions';
import { isAssetActuallyDeleted } from 'screens/editorial/asset/utility/assetFunctions';

export type MuiCloseEvent = Partial<Parameters<NonNullable<MenuProps['onClose']>>[0]>;
export type MuiCloseReasons = Partial<Parameters<NonNullable<MenuProps['onClose']>>[1]>;

export interface EditorialMiniMenuConfig {
  export?: { enabled?: boolean };
  edit?: { enabled?: boolean };
  liveView?: { enabled?: boolean };
  preview?: { enabled?: boolean };
  duplicate?: { enabled?: boolean };
  useTemplate?: { enabled?: boolean };
  delete?: { enabled?: boolean };
  purge?: { enabled?: boolean };
  restore?: { enabled?: boolean };
  clearSelections?: { enabled?: boolean };
  setAsPreview?: { enabled?: boolean };
  setAsCoverArt?: { enabled?: boolean };
  setAsPreviewLabel?: string; // i18n key for label
}

export const allEditorialMenuOptions: EditorialMiniMenuConfig = {
  export: { enabled: true },
  edit: { enabled: true },
  liveView: { enabled: true },
  preview: { enabled: true },
  duplicate: { enabled: true },
  useTemplate: { enabled: false },
  delete: { enabled: true },
  purge: { enabled: true },
  restore: { enabled: true },
  clearSelections: { enabled: true },
  setAsPreview: { enabled: false },
  setAsCoverArt: { enabled: false },
};

// Asset library options (delete disabled)
export const mediaLibraryMenuOptions: EditorialMiniMenuConfig = {
  export: { enabled: false },
  edit: { enabled: true },
  liveView: { enabled: true },
  preview: { enabled: true },
  duplicate: { enabled: true },
  useTemplate: { enabled: false },
  delete: { enabled: false },
  clearSelections: { enabled: true },
  setAsPreview: { enabled: false },
  setAsCoverArt: { enabled: false },
};

// Related assets drawer options
export const relatedAssetsMenuOptions: EditorialMiniMenuConfig = {
  export: { enabled: false },
  edit: { enabled: true },
  liveView: { enabled: false },
  preview: { enabled: false },
  duplicate: { enabled: false },
  delete: { enabled: false },
  clearSelections: { enabled: true },
  setAsPreview: { enabled: true },
  setAsCoverArt: { enabled: true },
};

// Related content drawer options
export const relatedContentMenuOptions: EditorialMiniMenuConfig = {
  export: { enabled: false },
  edit: { enabled: false }, // Note: Will link up edit modal in CMSADMIN-2509
  liveView: { enabled: false },
  preview: { enabled: false },
  duplicate: { enabled: false },
  delete: { enabled: false },
  clearSelections: { enabled: true },
  setAsPreview: { enabled: false },
  setAsCoverArt: { enabled: false },
};

// Template asset options - used when right-clicking a template in editorial search
export const templateMenuOptions: EditorialMiniMenuConfig = {
  export: { enabled: false },
  edit: { enabled: true },
  liveView: { enabled: false },
  preview: { enabled: false },
  duplicate: { enabled: false },
  useTemplate: { enabled: true },
  delete: { enabled: true },
  purge: { enabled: true },
  restore: { enabled: true },
  clearSelections: { enabled: true },
};

// GenericSearchItem[] but in here should safely
// be able to cast EditorialAssetSearchResultItem[]
export interface EditorialMiniMenuProps extends BaseMiniMenuProps {
  selectedItems: EditorialAssetSearchResultItem[];
  menuConfig?: EditorialMiniMenuConfig | null;
  handleExportSubmit?: (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    selectedItemIds: string[],
  ) => Promise<boolean>;
  contextMenuItem?: EditorialAssetSearchResultItem | null; // The item that was right-clicked
  isContextItemDisabled?: boolean; // True if the context item is disabled
  onSetPreview?: (asset: EditorialAssetSearchResultItem) => void;
  onSetCoverArt?: (asset: EditorialAssetSearchResultItem) => void;
}

/**
 * Related Tickets:
 * CMSADMIN-1270
 *
 * Notes:
 * Try to avoid specific hooks in this component and
 * instead pass in the data/functions you need as props, as
 * this menu may be used around various parts of the app and
 * the specific context may not be available in all cases.
 *
 * Edit (new tab)
 * opening multiple in new tab may not be possible
 * as browsers have popup blocking features
 * https://developer.mozilla.org/en-US/docs/Web/API/Window/open
 *
 * Duplicate: (new tab)
 * In blue UI, the duplicate injects a new row client side
 * and opens that asset in the edit modal. The blue client does this
 * because there is a slight delay between the search index
 * updating immedately after a duplication call. So if you
 * dupe and immediatley invalidate and refetch the search list
 * it may or may not show up in the list, really just depends on
 * the timings of the network. For our NXT UI, we are not
 * injecting a new row client side, we are just going to directly
 * navigate to the edit page of the asset that was duplicated.
 *
 *
 */

type ButtonState = {
  link?: string; // href action for some buttons
  show: boolean; // option visibility based on config and user permissions
} & PermissionInfo;

export const EditorialMiniMenu = memo(
  forwardRef<HTMLDivElement, EditorialMiniMenuProps>(
    (
      {
        menuEvent,
        selectedItems,
        handleMiniMenuClose,
        clearSelectedItems,
        raResource,
        menuConfig: menuConfigProp = allEditorialMenuOptions,
        contextMenuItem,
        isContextItemDisabled,
        onSetPreview,
        onSetCoverArt,
        anchorEl,
      },
      ref,
    ) => {
      // If the context item is a template, use template-specific menu options
      const isTemplateItem = Boolean(contextMenuItem?.template);
      const menuConfig = useMemo(() => {
        if (isTemplateItem) {
          return templateMenuOptions;
        }
        return menuConfigProp;
      }, [isTemplateItem, menuConfigProp]);

      const theme = useTheme();
      const notify = useNotify();
      const { t } = useTranslation();
      const { queryClient } = useCosmosQueryClient();
      const dataProvider = useDataProvider<BloxCustomDataProviderType>();
      const {
        export: { handleExportSubmit },
      } = useExportImport();
      const { activeSite, sessionConfig } = useCosmos();
      const siteTimeZone = sessionConfig?.time_zone;
      const {
        openQuickLaunch,
        modal: quickLaunchModal,
        isQuickLaunchAssetType,
      } = useAssetQuickLaunch();
      const [submitting, setSubmitting] = useState(false);

      // mutation - delete assets by uuid[]
      const { mutateAsync: mutateDelete, status: mutateDeleteStatus } = useMutation<
        PostEditorialAssetDeleteResult,
        ApiErrorResponse,
        PostEditorialAssetDeleteParams
      >({
        mutationKey: ['deleteAssetsByIds'],
        mutationFn: async (params) => {
          return await dataProvider.deleteAssetsByIds(params);
        },
        onSuccess: async (data, variables, context) => {
          const deletedUuids = new Set<string>(variables?.uuid ?? []);

          // Helper - filter deleted items out of a flat data array
          const filterItems = (items: Partial<FullEditorialAsset>[]) =>
            items.filter((item) => !item.uuid || !deletedUuids.has(item.uuid));

          // Optimistically remove items from regular getList cache entries
          queryClient.setQueriesData<any>(
            { queryKey: [raResource, 'getList'] },
            (oldData: TGetListResult<Partial<FullEditorialAsset>>) => {
              if (!oldData?.data) return oldData;
              const filtered = filterItems(oldData.data);
              const removedCount = oldData.data.length - filtered.length;
              return {
                ...oldData,
                data: filtered,
                total: Math.max(0, (oldData.total ?? 0) - removedCount),
              };
            },
          );

          // Optimistically remove items from getInfiniteList cache entries
          // (used by InfiniteListBase / editorial search)
          queryClient.setQueriesData<any>(
            { queryKey: [raResource, 'getInfiniteList'] },
            (oldData: InfiniteData<TGetListResult<Partial<FullEditorialAsset>>>) => {
              if (!oldData?.pages) return oldData;
              return {
                ...oldData,
                pages: oldData.pages.map(
                  (page: TGetListResult<Partial<FullEditorialAsset>>) => {
                    if (!page?.data) return page;
                    const filtered = filterItems(page.data);
                    const removedCount = page.data.length - filtered.length;
                    return {
                      ...page,
                      data: filtered,
                      total: Math.max(0, (page.total ?? 0) - removedCount),
                    };
                  },
                ),
              };
            },
          );

          // Mark queries as stale so the next user-triggered refetch goes to the
          // server, but do NOT refetch immediately - the backend search index
          // has a propagation delay and an immediate refetch would return
          // the wrong state (deleted items reappearing with their old status).
          queryClient.invalidateQueries({ queryKey: [raResource], refetchType: 'none' });
        },
        onError: async (error) => {
          notify(
            `${t('blox.general.error.label')}: ${error?.message || error?.axiosMessage}`,
            { type: 'error' },
          );
        },
      });

      // mutation - purge asset by uuid (one at a time per CDN throttle requirements)
      const { mutateAsync: mutatePurge, status: mutatePurgeStatus } = useMutation<
        PurgeEditorialAssetByIdResult,
        ApiErrorResponse,
        PurgeEditorialAssetByIdParams
      >({
        mutationKey: ['purgeAssetsById'],
        mutationFn: async (params) => {
          return await dataProvider.purgeAssetById(params);
        },
        onSuccess: async (data, variables, context) => {
          const purgedId = variables?.id;

          // Helper - filter the purged item out of a flat data array
          const filterItems = (items: Partial<FullEditorialAsset>[]) =>
            items.filter((item) => item.uuid !== purgedId);

          // Optimistically remove the item from regular getList cache entries
          queryClient.setQueriesData<any>(
            { queryKey: [raResource, 'getList'] },
            (oldData: TGetListResult<Partial<FullEditorialAsset>>) => {
              if (!oldData?.data) return oldData;
              const filtered = filterItems(oldData.data);
              const removedCount = oldData.data.length - filtered.length;
              return {
                ...oldData,
                data: filtered,
                total: Math.max(0, (oldData.total ?? 0) - removedCount),
              };
            },
          );

          // Optimistically remove the item from getInfiniteList cache entries
          // (used by InfiniteListBase / editorial search)
          queryClient.setQueriesData<any>(
            { queryKey: [raResource, 'getInfiniteList'] },
            (oldData: InfiniteData<TGetListResult<Partial<FullEditorialAsset>>>) => {
              if (!oldData?.pages) return oldData;
              return {
                ...oldData,
                pages: oldData.pages.map(
                  (page: TGetListResult<Partial<FullEditorialAsset>>) => {
                    if (!page?.data) return page;
                    const filtered = filterItems(page.data);
                    const removedCount = page.data.length - filtered.length;
                    return {
                      ...page,
                      data: filtered,
                      total: Math.max(0, (page.total ?? 0) - removedCount),
                    };
                  },
                ),
              };
            },
          );

          // Mark queries as stale so the next user-triggered refetch goes to the
          // server, but do NOT refetch immediately - the backend purge has a
          // propagation delay and an immediate refetch would return the item
          // as published (since the purge hasn't completed on the backend yet).
          queryClient.invalidateQueries({ queryKey: [raResource], refetchType: 'none' });
        },
        onError: async (error) => {
          notify(
            `${t('blox.general.error.label')}: ${error?.message || error?.axiosMessage}`,
            { type: 'error' },
          );
        },
      });

      // mutation - restore a deleted asset by uuid (one at a time)
      const { mutateAsync: mutateRestore, status: mutateRestoreStatus } = useMutation<
        RestoreEditorialAssetByIdResult,
        ApiErrorResponse,
        RestoreEditorialAssetByIdParams
      >({
        mutationKey: ['restoreAssetById'],
        mutationFn: async (params) => {
          return await dataProvider.restoreAssetById(params);
        },
        onSuccess: async (data, variables, context) => {
          const restoredId = variables?.id;

          // Helper - filter the restored item out of a flat data array
          // (removes it from "deleted" filtered views; stale invalidation handles the rest)
          const filterItems = (items: Partial<FullEditorialAsset>[]) =>
            items.filter((item) => item.uuid !== restoredId);

          // Optimistically remove the item from regular getList cache entries
          queryClient.setQueriesData<any>(
            { queryKey: [raResource, 'getList'] },
            (oldData: TGetListResult<Partial<FullEditorialAsset>>) => {
              if (!oldData?.data) return oldData;
              const filtered = filterItems(oldData.data);
              const removedCount = oldData.data.length - filtered.length;
              return {
                ...oldData,
                data: filtered,
                total: Math.max(0, (oldData.total ?? 0) - removedCount),
              };
            },
          );

          // Optimistically remove the item from getInfiniteList cache entries
          // (used by InfiniteListBase / editorial search)
          queryClient.setQueriesData<any>(
            { queryKey: [raResource, 'getInfiniteList'] },
            (oldData: InfiniteData<TGetListResult<Partial<FullEditorialAsset>>>) => {
              if (!oldData?.pages) {
                return oldData;
              }
              return {
                ...oldData,
                pages: oldData.pages.map(
                  (page: TGetListResult<Partial<FullEditorialAsset>>) => {
                    if (!page?.data) return page;
                    const filtered = filterItems(page.data);
                    const removedCount = page.data.length - filtered.length;
                    return {
                      ...page,
                      data: filtered,
                      total: Math.max(0, (page.total ?? 0) - removedCount),
                    };
                  },
                ),
              };
            },
          );

          // Mark queries as stale so the next user-triggered refetch goes to the
          // server, but do NOT refetch immediately - the restore has a propagation
          // delay and an immediate refetch may return stale state.
          queryClient.invalidateQueries({ queryKey: [raResource], refetchType: 'none' });
        },
        onError: async (error) => {
          notify(
            `${t('blox.general.error.label')}: ${error?.message || error?.axiosMessage}`,
            { type: 'error' },
          );
        },
      });

      // Duplicate an asset by uuid
      const { duplicate, status: dupStatus } = useDuplicateAsset({
        invalidateQueries: true,
        onError: (err) => {
          console.error('[EditorialMiniMenu] Duplicate error:', err);
          notify(`Error: ${err.message}`, { type: 'error' });
        },
      });

      // Determine menu mode: button (anchorEl) or context (menuEvent position)
      const isButtonMode = Boolean(anchorEl);

      // Minimenu UI States
      const menuX = menuEvent?.clientX ?? undefined;
      const menuY = menuEvent?.clientY ?? undefined;
      const hasXY = menuX && menuY;
      const isOpen = isButtonMode ? Boolean(anchorEl) : Boolean(hasXY);
      const anchorPos: MenuProps['anchorPosition'] = hasXY
        ? { top: menuY, left: menuX }
        : undefined;

      // Determine which items to use for menu operations
      // Check if the right-clicked item is part of the current selection
      const hasSelections = Boolean(selectedItems?.length);
      const contextItemId = contextMenuItem
        ? getRecordRealId(contextMenuItem, raResource)
        : null;
      const isContextItemSelected =
        hasSelections && contextItemId
          ? selectedItems.some(
              (item) => getRecordRealId(item, raResource) === contextItemId,
            )
          : false;

      // Right click behaviors:
      // 1. If items are selected AND the right-clicked item is one of them -> use all selected items (multi-item mode)
      // 2. If items are selected BUT the right-clicked item is NOT selected -> use only the right-clicked item (single-item mode)
      // 3. If no items are selected -> use the right-clicked item (single-item mode)
      const effectiveItems = useMemo(
        () =>
          hasSelections && isContextItemSelected
            ? selectedItems // Multi-item mode: use all selected items
            : contextMenuItem
              ? [contextMenuItem] // Single-item mode: use only the right-clicked item
              : [],
        [hasSelections, isContextItemSelected, selectedItems, contextMenuItem],
      );

      const selectedCount = effectiveItems?.length ?? 0;
      const hasMultipleSelected = Boolean(effectiveItems?.length > 1);
      const confirmDeleteCountStr = `${selectedCount} items will be deleted.`;
      const lastItem =
        effectiveItems?.length > 0
          ? effectiveItems[effectiveItems.length - 1]
          : undefined;
      const isDeleted = useMemo(
        () => isAssetActuallyDeleted(lastItem, siteTimeZone),
        [lastItem, siteTimeZone],
      );
      const duplicateModalLabel = useMemo(() => {
        if (!lastItem?.type || typeof lastItem.type !== 'string') {
          return 'Asset';
        }
        const label = formatAssetString(lastItem.type.trim(), t);
        return label ? label.charAt(0).toUpperCase() + label.slice(1) : 'Asset';
      }, [lastItem?.type, t]);
      const canUseQuickLaunchForDuplicate = useMemo(() => {
        if (!lastItem?.type) {
          return false;
        }
        return isQuickLaunchAssetType(lastItem.type);
      }, [isQuickLaunchAssetType, lastItem?.type]);
      const buildDuplicatePrefill = useCallback((prefill?: AssetQuickLaunchPrefill) => {
        if (!prefill || Object.keys(prefill).length === 0) {
          return undefined;
        }

        return prefill;
      }, []);

      // Permission Checks
      const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
      const { granted: userCanCreate, name: createPermName } =
        checkCreateAssetsPermission(sessionConfig);
      const { granted: userCanDelete, name: deletePermName } =
        checkDeleteAssetsPermission(sessionConfig);
      const hasCreatePerm = Boolean(userIsAdmin || userCanCreate);
      const hasDeletePerm = Boolean(userIsAdmin || userCanDelete);

      // --------------------------------------------------
      // Event Handlers, Styles
      // --------------------------------------------------
      const iconSx = useMemo(() => getMiniMenuIconSx(theme), [theme]);

      // for subsequent right clicks on background items
      // while menu open we will do some tricks hiding backdrop,
      // pass pointer events through, removing transitions
      // and add a few capture listeners to handle event/close
      // also tried this with <Popper> as well but the scroll locking
      // feature that menu has felt better than without it
      const menuListRef = useRef<HTMLUListElement>(null);

      const internalOnClose = useCallback(
        (event?: MuiCloseEvent, reason?: MuiCloseReasons) => {
          handleMiniMenuClose();
        },
        [handleMiniMenuClose],
      );

      const handleMenuClickCaptures = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
          // if click is outside the menu prevent bubbling/default
          // and then forward to internalOnClose to handle close
          if (!menuListRef?.current?.contains(e?.target as Node)) {
            e?.stopPropagation?.();
            e?.preventDefault?.();
            internalOnClose(e, 'backdropClick');
            return;
          }
          // else - the click happened inside the menu
          // so let event pass to menu click handlers
          // and have them handle event as needed
        },
        [internalOnClose, menuListRef],
      );

      // --------------------------------------------------
      // Live View Button
      // --------------------------------------------------

      const liveViewState: ButtonState = useMemo(() => {
        const showLiveView = !isDeleted && Boolean(menuConfig?.liveView?.enabled);
        if (hasMultipleSelected || !lastItem?.url) {
          return {
            disabled: true,
            show: showLiveView,
          };
        }
        return {
          disabled: false,
          link: lastItem?.url,
          show: showLiveView,
        };
      }, [hasMultipleSelected, lastItem?.url, menuConfig?.liveView?.enabled, isDeleted]);

      const handleLiveView = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault(); // menu item has href attached for ctrl+clicks
          if (liveViewState?.disabled || !liveViewState?.link) return;
          window.open(liveViewState?.link, '_blank');
          internalOnClose();
        },
        [liveViewState, internalOnClose],
      );

      // --------------------------------------------------
      // Preview Button
      // --------------------------------------------------

      const previewState: ButtonState = useMemo(() => {
        const showPreview = !isDeleted && Boolean(menuConfig?.preview?.enabled);
        const canPreview = Boolean(lastItem?.uuid && lastItem?.status !== 'deleted');
        if (hasMultipleSelected || !canPreview) {
          return {
            disabled: true,
            show: showPreview,
          };
        }
        return {
          disabled: false,
          link: `/${activeSite}/editorial/preview/${lastItem?.uuid}`,
          show: showPreview,
        };
      }, [
        activeSite,
        hasMultipleSelected,
        lastItem?.status,
        lastItem?.uuid,
        menuConfig?.preview?.enabled,
        isDeleted,
      ]);

      const handlePreview = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          if (previewState?.disabled || !previewState?.link) return;
          window.open(previewState.link, '_blank');
          internalOnClose();
        },
        [previewState, internalOnClose],
      );

      // --------------------------------------------------
      // Edit Button
      // --------------------------------------------------

      const editState: ButtonState = useMemo(() => {
        const showEdit = Boolean(menuConfig?.edit?.enabled);
        const editorEnabled = isAssetTypeEditorEnabled(lastItem?.type);
        const canEditItem = Boolean(lastItem?.uuid && editorEnabled);
        if (hasMultipleSelected || !canEditItem) {
          return {
            disabled: true,
            show: showEdit,
          };
        }
        return {
          disabled: false,
          link: `/${activeSite}/editorial/edit/${lastItem?.uuid}`,
          show: showEdit,
        };
      }, [activeSite, hasMultipleSelected, lastItem, menuConfig?.edit?.enabled]);

      // --------------------------------------------------
      // Export Button
      // --------------------------------------------------

      const exportState: ButtonState = useMemo(() => {
        const showExport = !isDeleted && Boolean(menuConfig?.export?.enabled);
        const canExport = Boolean(effectiveItems.length > 0);
        if (!canExport) {
          return {
            disabled: true,
            show: showExport,
          };
        }
        return {
          disabled: false,
          show: showExport,
        };
      }, [effectiveItems.length, menuConfig?.export?.enabled, isDeleted]);

      const handleExport = useCallback(
        async (event: React.MouseEvent<HTMLLIElement>) => {
          event?.preventDefault();
          if (exportState?.disabled) return;
          const uuids = effectiveItems
            .map((item) => getRecordRealId(item, raResource))
            .filter(Boolean);
          if (!uuids?.length) {
            console.error('[EditorialMiniMenu] No uuids to export.');
            internalOnClose();
            return;
          }
          setSubmitting(true);
          const success = await handleExportSubmit?.(event, uuids);
          if (success) {
            internalOnClose();
          }
          setSubmitting(false);
        },
        [
          exportState?.disabled,
          effectiveItems,
          raResource,
          handleExportSubmit,
          internalOnClose,
        ],
      );

      // --------------------------------------------------
      // Duplicate Button
      // --------------------------------------------------

      const duplicateState: ButtonState = useMemo(() => {
        const showDuplicate =
          !isDeleted && hasCreatePerm && Boolean(menuConfig?.duplicate?.enabled);
        const editorEnabled = isAssetTypeEditorEnabled(lastItem?.type);
        const canDuplicate = Boolean(
          hasCreatePerm &&
          lastItem?.uuid &&
          lastItem?.status !== 'deleted' &&
          editorEnabled &&
          dupStatus !== 'pending', // v5: 'pending' replaces legacy 'loading'
        );
        if (hasMultipleSelected || !canDuplicate) {
          const reason = !hasCreatePerm
            ? t('blox.permissions.msg.required_short', { name: createPermName })
            : undefined;
          return {
            disabled: true,
            info: reason,
            show: showDuplicate,
          };
        }
        return {
          disabled: false,
          show: showDuplicate,
        };
      }, [
        t,
        hasCreatePerm,
        createPermName,
        hasMultipleSelected,
        lastItem?.status,
        lastItem?.type,
        lastItem?.uuid,
        menuConfig?.duplicate?.enabled,
        dupStatus,
        isDeleted,
      ]);

      const runDuplicate = useCallback(
        async (prefill?: AssetQuickLaunchPrefill) => {
          const uuid = lastItem?.uuid;
          const title = lastItem?.title;

          if (!uuid || !title) {
            console.warn('[EditorialMiniMenu] Missing data for duplicate.', {
              uuid,
              title,
            });
            return;
          }

          await duplicate({
            id: uuid,
            title,
            quickLaunchPrefill: buildDuplicatePrefill(prefill),
          });
          notify('Asset duplicated.', { type: 'success' });
        },
        [buildDuplicatePrefill, duplicate, lastItem?.title, lastItem?.uuid, notify],
      );

      const handleDuplicate = useCallback(
        async (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();

          if (duplicateState?.disabled) {
            console.warn('[EditorialMiniMenu] Duplicate disabled.');
            internalOnClose();
            return;
          }

          if (canUseQuickLaunchForDuplicate && lastItem?.type) {
            openQuickLaunch({
              assetType: lastItem.type,
              assetLabel: duplicateModalLabel,
              mode: 'duplicate',
              onSubmit: (prefill) => {
                runDuplicate(prefill).catch((error) => {
                  console.error('[EditorialMiniMenu] duplicate threw error', error);
                });
              },
            });
            internalOnClose();
            return;
          }

          try {
            await runDuplicate();
          } catch (error) {
            console.error('[EditorialMiniMenu] duplicate threw error', error);
          } finally {
            internalOnClose();
          }
        },
        [
          canUseQuickLaunchForDuplicate,
          duplicateModalLabel,
          duplicateState?.disabled,
          internalOnClose,
          lastItem?.type,
          openQuickLaunch,
          runDuplicate,
        ],
      );

      // --------------------------------------------------
      // Use Template Button
      // Creates a new asset from template (sets template: false)
      // --------------------------------------------------

      const useTemplateState: ButtonState = useMemo(() => {
        const showUseTemplate =
          hasCreatePerm && Boolean(menuConfig?.useTemplate?.enabled);
        const editorEnabled = isAssetTypeEditorEnabled(lastItem?.type);
        const canUseTemplate = Boolean(
          hasCreatePerm &&
          lastItem?.uuid &&
          lastItem?.status !== 'deleted' &&
          editorEnabled &&
          dupStatus !== 'pending',
        );
        if (hasMultipleSelected || !canUseTemplate) {
          const reason = !hasCreatePerm
            ? t('blox.permissions.msg.required_short', { name: createPermName })
            : undefined;
          return {
            disabled: true,
            info: reason,
            show: showUseTemplate,
          };
        }
        return {
          disabled: false,
          show: showUseTemplate,
        };
      }, [
        t,
        hasCreatePerm,
        createPermName,
        hasMultipleSelected,
        lastItem?.status,
        lastItem?.type,
        lastItem?.uuid,
        menuConfig?.useTemplate?.enabled,
        dupStatus,
      ]);

      const runUseTemplate = useCallback(
        async (prefill?: AssetQuickLaunchPrefill) => {
          const uuid = lastItem?.uuid;
          const title = lastItem?.title;

          if (!uuid || !title) {
            console.warn('[EditorialMiniMenu] Missing data for use template.', {
              uuid,
              title,
            });
            return;
          }

          await duplicate({
            id: uuid,
            title,
            quickLaunchPrefill: buildDuplicatePrefill(prefill),
            additionalPatches: [{ path: '/template', op: 'add', value: false }],
            isTemplateDuplication: true,
          });
        },
        [buildDuplicatePrefill, duplicate, lastItem?.title, lastItem?.uuid],
      );

      const handleUseTemplate = useCallback(
        async (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();

          if (useTemplateState?.disabled) {
            console.warn('[EditorialMiniMenu] Use template disabled.');
            internalOnClose();
            return;
          }

          if (canUseQuickLaunchForDuplicate && lastItem?.type) {
            openQuickLaunch({
              assetType: lastItem.type,
              assetLabel: duplicateModalLabel,
              mode: 'use-template',
              currentTitle: lastItem.title,
              defaultWorkflowId: lastItem?.workflow_id || undefined,
              defaultStartTime: lastItem?.start_time || undefined,
              defaultSlug: lastItem?.slug || undefined,
              onSubmit: (prefill) => {
                runUseTemplate(prefill).catch((error) => {
                  console.error('[EditorialMiniMenu] use template threw error', error);
                });
              },
            });
            internalOnClose();
            return;
          }

          try {
            await runUseTemplate();
          } catch (error) {
            console.error('[EditorialMiniMenu] use template threw error', error);
          } finally {
            internalOnClose();
          }
        },
        [
          canUseQuickLaunchForDuplicate,
          duplicateModalLabel,
          internalOnClose,
          lastItem,
          openQuickLaunch,
          runUseTemplate,
          useTemplateState?.disabled,
        ],
      );

      // --------------------------------------------------
      // Delete Button
      // delete endpoint uses uuid array so
      // it can be used for multiple items
      // --------------------------------------------------

      const deleteBtnState: ButtonState = useMemo(() => {
        const isDeleted = isAssetActuallyDeleted(lastItem, siteTimeZone);
        const showDelete =
          !isDeleted && hasDeletePerm && Boolean(menuConfig?.delete?.enabled);

        // Prevent deletion of current asset being viewed in modal
        if (isContextItemDisabled && !isContextItemSelected) {
          return {
            disabled: true,
            show: showDelete,
          };
        }

        // edge case: single selection that is already deleted (including scheduled deletions that have passed)
        const alreadyDeleted =
          selectedCount === 1 && isAssetActuallyDeleted(lastItem, siteTimeZone);
        if (alreadyDeleted) {
          return {
            disabled: true,
            show: showDelete,
          };
        }
        // general can delete checks
        const canDelete = Boolean(
          effectiveItems.length > 0 && mutateDeleteStatus !== 'pending',
        );
        if (!canDelete) {
          const reason = !hasDeletePerm
            ? t('blox.permissions.msg.required_short', { name: deletePermName })
            : undefined;
          return {
            disabled: true,
            info: reason,
            show: showDelete,
          };
        }
        // has selections and delete perms
        return {
          disabled: false,
          show: showDelete,
        };
      }, [
        t,
        deletePermName,
        hasDeletePerm,
        effectiveItems.length,
        lastItem,
        siteTimeZone,
        menuConfig?.delete?.enabled,
        mutateDeleteStatus,
        selectedCount,
        isContextItemDisabled,
        isContextItemSelected,
      ]);

      const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

      const openDeleteDialog = useCallback(() => {
        setConfirmDeleteOpen(true);
        internalOnClose();
      }, [internalOnClose]);

      const closeDeleteDialog = useCallback(() => {
        setConfirmDeleteOpen(false);
      }, []);

      // confirm delete action in dialog
      const handleDeleteConfirm = useCallback(async () => {
        if (!hasDeletePerm) {
          notify(t('blox.permissions.msg.required_short', { name: deletePermName }), {
            type: 'error',
          });
          internalOnClose();
          return;
        }
        const uuids = effectiveItems
          .map((item) => getRecordRealId(item, raResource))
          .filter(Boolean);
        if (!uuids?.length) {
          console.error('[handleDeleteAssets] No uuids to process.');
          internalOnClose();
          return;
        }
        // delete the items
        try {
          await mutateDelete({ uuid: uuids });
          notify('Delete request received.', { type: 'success' });
        } catch (e) {
          console.error('[handleDeleteConfirm] Error:', e);
        } finally {
          clearSelectedItems();
          internalOnClose();
        }
      }, [
        t,
        raResource,
        notify,
        effectiveItems,
        mutateDelete,
        clearSelectedItems,
        internalOnClose,
        hasDeletePerm,
        deletePermName,
      ]);

      // cancel delete action in dialog
      const handleDeleteCancel = useCallback(() => {
        closeDeleteDialog();
      }, [closeDeleteDialog]);

      // open delete dialog from minimenu
      const handleDeleteMenuClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          if (deleteBtnState?.disabled) return;
          openDeleteDialog();
        },
        [deleteBtnState?.disabled, openDeleteDialog],
      );

      // --------------------------------------------------
      // Purge Button
      // purge endpoint uses uuid array so
      // it can be used for multiple items
      // --------------------------------------------------

      const purgeBtnState: ButtonState = useMemo(() => {
        const isDeleted = isAssetActuallyDeleted(lastItem, siteTimeZone);
        // Per requirements: purge is one-at-a-time and only for already-deleted assets
        const showPurge =
          hasDeletePerm && Boolean(menuConfig?.purge?.enabled) && isDeleted;

        if (!showPurge) {
          const reason = !hasDeletePerm
            ? t('blox.permissions.msg.required_short', { name: deletePermName })
            : undefined;
          return {
            disabled: true,
            info: reason,
            show: false,
          };
        }

        return {
          disabled: mutatePurgeStatus === 'pending' || hasMultipleSelected,
          show: true,
        };
      }, [
        t,
        deletePermName,
        hasDeletePerm,
        lastItem,
        siteTimeZone,
        menuConfig?.purge?.enabled,
        mutatePurgeStatus,
        hasMultipleSelected,
      ]);

      const [confirmPurgeOpen, setConfirmPurgeOpen] = useState(false);

      const openPurgeDialog = useCallback(() => {
        setConfirmPurgeOpen(true);
        internalOnClose();
      }, [internalOnClose]);

      const closePurgeDialog = useCallback(() => {
        setConfirmPurgeOpen(false);
      }, []);

      // confirm purge action in dialog
      const handlePurgeConfirm = useCallback(async () => {
        if (!hasDeletePerm) {
          notify(t('blox.permissions.msg.required_short', { name: deletePermName }), {
            type: 'error',
          });
          internalOnClose();
          return;
        }
        const uuid = getRecordRealId(lastItem, raResource);
        if (!uuid) {
          console.error('[handlePurgeConfirm] No uuid to process.');
          internalOnClose();
          return;
        }
        // purge the items
        try {
          await mutatePurge({ id: uuid });
          notify('Purge request received.', { type: 'success' });
        } catch (e) {
          console.error('[handlePurgeConfirm] Error:', e);
        } finally {
          clearSelectedItems();
          internalOnClose();
        }
      }, [
        t,
        raResource,
        notify,
        lastItem,
        mutatePurge,
        clearSelectedItems,
        internalOnClose,
        hasDeletePerm,
        deletePermName,
      ]);

      // cancel purge action in dialog
      const handlePurgeCancel = useCallback(() => {
        closePurgeDialog();
      }, [closePurgeDialog]);

      // open purge dialog from minimenu
      const handlePurgeMenuClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          if (purgeBtnState?.disabled) return;
          openPurgeDialog();
        },
        [purgeBtnState?.disabled, openPurgeDialog],
      );

      // --------------------------------------------------
      // Restore Button
      // restore endpoint is PUT /editorial/asset/{id}/restore/
      // supports single and multi-select, only for already-deleted assets
      // --------------------------------------------------

      const restoreItems = useMemo(
        () =>
          Array.isArray(selectedItems) && selectedItems.length > 0
            ? selectedItems
            : contextMenuItem
              ? [contextMenuItem]
              : [],
        [selectedItems, contextMenuItem],
      );

      const deletedRestoreItems = useMemo(
        () => restoreItems.filter((item) => isAssetActuallyDeleted(item, siteTimeZone)),
        [restoreItems, siteTimeZone],
      );

      const restoreSubTxt = useMemo(() => {
        if (deletedRestoreItems.length === 1) {
          return t('blox.modal.simple_confirm_delete.undo_deletion.description', {
            name: truncateString(deletedRestoreItems[0]?.title ?? 'this item', 25),
          });
        }

        return t('blox.modal.simple_confirm_delete.undo_deletion.description_multiple', {
          count: deletedRestoreItems.length,
        });
      }, [deletedRestoreItems, t]);

      const restoreBtnState: ButtonState = useMemo(() => {
        const showRestore =
          hasDeletePerm &&
          Boolean(menuConfig?.restore?.enabled) &&
          deletedRestoreItems.length > 0;

        if (!showRestore) {
          const reason = !hasDeletePerm
            ? t('blox.permissions.msg.required_short', { name: deletePermName })
            : undefined;
          return {
            disabled: true,
            info: reason,
            show: false,
          };
        }

        return {
          disabled: mutateRestoreStatus === 'pending',
          show: true,
        };
      }, [
        t,
        deletePermName,
        hasDeletePerm,
        deletedRestoreItems.length,
        menuConfig?.restore?.enabled,
        mutateRestoreStatus,
      ]);

      const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);

      const openRestoreDialog = useCallback(() => {
        setConfirmRestoreOpen(true);
        internalOnClose();
      }, [internalOnClose]);

      const closeRestoreDialog = useCallback(() => {
        setConfirmRestoreOpen(false);
      }, []);

      // confirm restore action in dialog
      const handleRestoreConfirm = useCallback(async () => {
        if (!hasDeletePerm) {
          notify(t('blox.permissions.msg.required_short', { name: deletePermName }), {
            type: 'error',
          });
          internalOnClose();
          return;
        }

        const restoreIds = deletedRestoreItems
          .map((item) => getRecordRealId(item, raResource))
          .filter((id): id is string => Boolean(id));

        if (restoreIds.length === 0) {
          console.error('[handleRestoreConfirm] No uuids to process.');
          internalOnClose();
          return;
        }

        let restoredCount = 0;
        let failedCount = 0;

        try {
          for (const id of restoreIds) {
            try {
              await mutateRestore({ id });
              restoredCount += 1;
            } catch (error) {
              failedCount += 1;
              console.error(`[handleRestoreConfirm] Error restoring asset ${id}:`, error);
            }
          }

          if (restoredCount > 0) {
            notify(
              restoredCount === 1
                ? t('blox.actions.restore_requested')
                : `${restoredCount} restore requests received.`,
              { type: 'success' },
            );
          }

          if (failedCount > 0) {
            notify(
              `${failedCount} restore request${failedCount > 1 ? 's' : ''} failed.`,
              { type: 'error' },
            );
          }
        } catch (e) {
          console.error('[handleRestoreConfirm] Error:', e);
        } finally {
          clearSelectedItems();
          internalOnClose();
        }
      }, [
        t,
        raResource,
        notify,
        deletedRestoreItems,
        mutateRestore,
        clearSelectedItems,
        internalOnClose,
        hasDeletePerm,
        deletePermName,
      ]);

      // cancel restore action in dialog
      const handleRestoreCancel = useCallback(() => {
        closeRestoreDialog();
      }, [closeRestoreDialog]);

      // open restore dialog from minimenu
      const handleRestoreMenuClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          if (restoreBtnState?.disabled) {
            return;
          }
          openRestoreDialog();
        },
        [restoreBtnState?.disabled, openRestoreDialog],
      );

      // --------------------------------------------------
      // Set As Preview / Cover Art
      // --------------------------------------------------

      const setAsPreviewState: ButtonState = useMemo(() => {
        const allowedType = lastItem?.type ? lastItem.type === 'image' : false;
        const show = Boolean(
          menuConfig?.setAsPreview?.enabled && onSetPreview && allowedType,
        );
        const disabled = hasMultipleSelected || !lastItem?.uuid || !onSetPreview;
        return {
          disabled,
          show,
        };
      }, [
        hasMultipleSelected,
        lastItem?.type,
        lastItem?.uuid,
        menuConfig?.setAsPreview?.enabled,
        onSetPreview,
      ]);

      const handleSetAsPreview = useCallback(
        (event: React.MouseEvent<HTMLLIElement>) => {
          event?.preventDefault();

          if (setAsPreviewState?.disabled || !lastItem || !onSetPreview) {
            internalOnClose();
            return;
          }

          onSetPreview(lastItem);
          internalOnClose();
        },
        [setAsPreviewState?.disabled, lastItem, onSetPreview, internalOnClose],
      );

      const setAsCoverArtState: ButtonState = useMemo(() => {
        const allowedType = lastItem?.type ? lastItem.type === 'image' : false;
        const show = Boolean(
          menuConfig?.setAsCoverArt?.enabled && onSetCoverArt && allowedType,
        );
        const disabled = hasMultipleSelected || !lastItem?.uuid || !onSetCoverArt;
        return {
          disabled,
          show,
        };
      }, [
        hasMultipleSelected,
        lastItem?.type,
        lastItem?.uuid,
        menuConfig?.setAsCoverArt?.enabled,
        onSetCoverArt,
      ]);

      const handleSetAsCoverArt = useCallback(
        (event: React.MouseEvent<HTMLLIElement>) => {
          event?.preventDefault();

          if (setAsCoverArtState?.disabled || !lastItem || !onSetCoverArt) {
            internalOnClose();
            return;
          }

          onSetCoverArt(lastItem);
          internalOnClose();
        },
        [setAsCoverArtState?.disabled, lastItem, onSetCoverArt, internalOnClose],
      );

      // --------------------------------------------------
      // Clear Button
      // --------------------------------------------------

      const clearButtonState: ButtonState = useMemo(() => {
        const showClear = Boolean(menuConfig?.clearSelections?.enabled);
        const hasSelections = Array.isArray(selectedItems) && selectedItems.length > 0;
        return {
          disabled: !hasSelections,
          show: showClear,
        };
      }, [menuConfig?.clearSelections?.enabled, selectedItems]);

      const handleClearSelections = useCallback(() => {
        clearSelectedItems();
        internalOnClose();
      }, [internalOnClose, clearSelectedItems]);

      // Determine if there are any visible menu options above clear selections
      const optionStates = [
        editState,
        liveViewState,
        previewState,
        duplicateState,
        setAsPreviewState,
        setAsCoverArtState,
        useTemplateState,
        deleteBtnState,
        purgeBtnState,
        restoreBtnState,
      ];
      const hasOptions = optionStates.some((state) => state?.show);

      return (
        <Fragment>
          <SimpleConfirmDeleteModal
            open={confirmDeleteOpen}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            subTxt={confirmDeleteCountStr}
            confirmBtnDisabled={mutateDeleteStatus === 'pending'}
            cancelBtnDisabled={mutateDeleteStatus === 'pending'}
            showLoadingBar={mutateDeleteStatus === 'pending'}
            initialWidth={350}
            initialHeight={215}
            isDraggable={true}
          />
          <SimpleConfirmDeleteModal
            open={confirmPurgeOpen}
            onConfirm={handlePurgeConfirm}
            onCancel={handlePurgeCancel}
            headerTxt={t('blox.modal.simple_confirm_delete.purge.header')}
            titleTxt={t('blox.modal.simple_confirm_delete.purge.title')}
            subTxt={t('blox.modal.simple_confirm_delete.purge.description', {
              name: truncateString(lastItem?.title ?? 'this item', 25),
            })}
            cancelButtonText={t('blox.modal.simple_confirm_delete.purge.cancel')}
            confirmButtonText={t('blox.modal.simple_confirm_delete.purge.confirm')}
            confirmBtnDisabled={mutateDeleteStatus === 'pending'}
            cancelBtnDisabled={mutateDeleteStatus === 'pending'}
            showLoadingBar={mutateDeleteStatus === 'pending'}
            initialWidth={550}
            initialHeight={250}
            isDraggable={true}
          />
          <SimpleConfirmDeleteModal
            open={confirmRestoreOpen}
            onConfirm={handleRestoreConfirm}
            onCancel={handleRestoreCancel}
            headerTxt={t('blox.modal.simple_confirm_delete.undo_deletion.header')}
            titleTxt={t('blox.modal.simple_confirm_delete.undo_deletion.title')}
            subTxt={restoreSubTxt}
            cancelButtonText={t('blox.modal.simple_confirm_delete.undo_deletion.cancel')}
            confirmButtonText={t(
              'blox.modal.simple_confirm_delete.undo_deletion.confirm',
            )}
            confirmBtnDisabled={mutateRestoreStatus === 'pending'}
            cancelBtnDisabled={mutateRestoreStatus === 'pending'}
            showLoadingBar={mutateRestoreStatus === 'pending'}
            initialWidth={550}
            initialHeight={250}
            isDraggable={true}
          />
          <Menu
            ref={ref}
            id="editorial-minimenu"
            data-testid="editorial-minimenu"
            disableEnforceFocus={true}
            open={isOpen}
            onClose={internalOnClose}
            sx={miniMenuSx}
            hideBackdrop={true}
            onMouseDownCapture={handleMenuClickCaptures}
            onContextMenuCapture={handleMenuClickCaptures}
            {...(isButtonMode
              ? { anchorEl }
              : {
                  anchorReference: 'anchorPosition' as const,
                  anchorPosition: anchorPos,
                  TransitionProps: { timeout: { enter: 0, exit: 0 } },
                })}
            slotProps={{
              list: {
                autoFocusItem: true,
                ref: menuListRef,
              },
            }}
          >
            {editState?.show ? (
              <OpenInNewButton
                asMenuItem
                href={editState.link || '#'}
                onIconClick={internalOnClose}
                testId="editorial-minimenu-edit-in-new-tab"
                disabled={!editState.link || editState?.disabled}
              />
            ) : null}

            {liveViewState?.show ? (
              <MenuItem
                id="editorial-minimenu-live-view"
                data-testid="editorial-minimenu-live-view"
                component="a"
                target="_blank"
                href={liveViewState?.link}
                disabled={liveViewState?.disabled}
                onClick={handleLiveView}
              >
                <PublicIcon sx={iconSx} />
                {t('blox.general.live_view')}
              </MenuItem>
            ) : null}

            {previewState?.show ? (
              <MenuItem
                id="editorial-minimenu-preview"
                data-testid="editorial-minimenu-preview"
                component="a"
                target="_blank"
                href={previewState?.link}
                disabled={previewState?.disabled}
                onClick={handlePreview}
              >
                <PreviewIcon sx={iconSx} />
                {t('blox.general.preview')}
              </MenuItem>
            ) : null}

            {duplicateState?.show ? (
              <MenuItem
                id="editorial-minimenu-duplicate"
                data-testid="editorial-minimenu-duplicate"
                onClick={handleDuplicate}
                disabled={duplicateState?.disabled}
              >
                <CopyAllIcon sx={iconSx} />
                {t('blox.actions.duplicate')}
              </MenuItem>
            ) : null}

            {setAsPreviewState?.show ? (
              <MenuItem
                id="editorial-minimenu-set-as-preview"
                data-testid="editorial-minimenu-set-as-preview"
                onClick={handleSetAsPreview}
                disabled={setAsPreviewState?.disabled}
              >
                <ImageIcon sx={iconSx} />
                {t(menuConfig?.setAsPreviewLabel || 'blox.actions.set_as_preview')}
              </MenuItem>
            ) : null}

            {setAsCoverArtState?.show ? (
              <MenuItem
                id="editorial-minimenu-set-as-cover-art"
                data-testid="editorial-minimenu-set-as-cover-art"
                onClick={handleSetAsCoverArt}
                disabled={setAsCoverArtState?.disabled}
              >
                <WallpaperIcon sx={iconSx} />
                {t('blox.actions.set_as_cover_art')}
              </MenuItem>
            ) : null}

            {useTemplateState?.show ? (
              <MenuItem
                id="editorial-minimenu-use-template"
                data-testid="editorial-minimenu-use-template"
                onClick={handleUseTemplate}
                disabled={useTemplateState?.disabled}
              >
                <ContentCopyIcon sx={iconSx} />
                {t('blox.editorial.moreActions.useTemplate')}
              </MenuItem>
            ) : null}

            {exportState?.show ? (
              <MenuItem
                id="editorial-minimenu-export"
                data-testid="editorial-minimenu-export"
                disabled={submitting || exportState?.disabled}
                onClick={handleExport}
              >
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <DownloadIcon sx={iconSx} />
                  {submitting && (
                    <CircularProgress
                      size={28}
                      sx={{
                        position: 'absolute',
                        top: -2,
                        left: -2,
                      }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  {t('blox.general.export')}
                </Box>
              </MenuItem>
            ) : null}

            {deleteBtnState?.show ? (
              <MenuItem
                id="editorial-minimenu-delete"
                data-testid="editorial-minimenu-delete"
                onClick={handleDeleteMenuClick}
                disabled={deleteBtnState?.disabled}
              >
                <DeleteIcon sx={iconSx} />
                {t('blox.actions.delete')}
              </MenuItem>
            ) : null}

            {restoreBtnState?.show ? (
              <MenuItem
                id="editorial-minimenu-restore"
                data-testid="editorial-minimenu-restore"
                onClick={handleRestoreMenuClick}
                disabled={restoreBtnState?.disabled}
              >
                <RestoreFromTrashIcon sx={{ ...iconSx }} />
                {t('blox.actions.undo_deletion')}
              </MenuItem>
            ) : null}

            {purgeBtnState?.show ? (
              <MenuItem
                id="editorial-minimenu-purge"
                data-testid="editorial-minimenu-purge"
                onClick={handlePurgeMenuClick}
                disabled={purgeBtnState?.disabled}
              >
                <CleaningServicesIcon sx={{ ...iconSx }} />
                {t('blox.actions.purge')}
              </MenuItem>
            ) : null}

            {/* Only show divider if there is at least one other visible menu item above clear selections */}
            {clearButtonState?.show && hasOptions ? <Divider /> : null}

            {clearButtonState?.show ? (
              <MenuItem
                id="editorial-minimenu-clear-selections"
                data-testid="editorial-minimenu-clear-selections"
                onClick={handleClearSelections}
                disabled={clearButtonState.disabled}
              >
                <ClearAllIcon sx={iconSx} />
                {t('blox.batch_edit.actions.clear')}
              </MenuItem>
            ) : null}
          </Menu>
          {quickLaunchModal}
        </Fragment>
      );
    },
  ),
);

EditorialMiniMenu.displayName = 'EditorialMiniMenu';
